import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from '../services/logger.ts';

interface TrafficMonitorOptions {
  trackPageViews?: boolean;
  trackApiCalls?: boolean;
  customRoute?: string;
}

export function useTrafficMonitor(options: TrafficMonitorOptions = {}) {
  const location = useLocation();
  const lastTrackedRoute = useRef<string>('');
  const {
    trackPageViews = true,
    trackApiCalls = false,
    customRoute
  } = options;

  // Track page views
  useEffect(() => {
    if (!trackPageViews) return;

    const currentRoute = customRoute || location.pathname;
    
    // Avoid duplicate tracking for the same route
    if (currentRoute === lastTrackedRoute.current) return;
    
    lastTrackedRoute.current = currentRoute;
    
    // Track the page view
    logger.trackRequest(currentRoute, {
      type: 'page_view',
      search: location.search,
      hash: location.hash,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
    });

    logger.info('Page view tracked', {
      route: currentRoute,
      fullPath: `${currentRoute}${location.search}${location.hash}`,
    });

  }, [location, trackPageViews, customRoute]);

  // Return function to manually track API calls
  const trackApiCall = (endpoint: string, method: string = 'GET', additionalData?: Record<string, unknown>) => {
    if (!trackApiCalls) return;

    logger.trackRequest(`API:${method}:${endpoint}`, {
      type: 'api_call',
      method,
      endpoint,
      timestamp: new Date().toISOString(),
      ...additionalData,
    });

    logger.debug('API call tracked', {
      endpoint,
      method,
      ...additionalData,
    });
  };

  return {
    trackApiCall,
  };
}

// Higher-order function to wrap API calls with monitoring
export function withTrafficMonitoring<T extends (...args: unknown[]) => Promise<unknown>>(
  apiFunction: T,
  endpoint: string,
  method: string = 'POST'
): T {
  return (async (...args: Parameters<T>) => {
    const startTime = Date.now();
    
    try {
      // Track the API call start
      logger.trackRequest(`API:${method}:${endpoint}`, {
        type: 'api_call_start',
        method,
        endpoint,
        timestamp: new Date().toISOString(),
      });

      const result = await apiFunction(...args);
      
      // Track successful completion
      const duration = Date.now() - startTime;
      logger.trackRequest(`API:${method}:${endpoint}:success`, {
        type: 'api_call_success',
        method,
        endpoint,
        duration,
        timestamp: new Date().toISOString(),
      });

      return result;
    } catch (error) {
      // Track failed API call
      const duration = Date.now() - startTime;
      logger.trackRequest(`API:${method}:${endpoint}:error`, {
        type: 'api_call_error',
        method,
        endpoint,
        duration,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });

      logger.error(`API call failed: ${method} ${endpoint}`, {
        duration,
        error,
      });

      throw error;
    }
  }) as T;
} 